import { useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { Box, FormControlLabel, Stack, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
    StyledDialog,
    StyledDialogContainer
} from '@/components/consent/StyledDialog';
import {
    OutlinedSecondaryButtonLG,
    PrimaryButtonLG
} from '@/components/shared/Button';
import { InputText } from '@/components/shared/InputText';
import { Dropdown } from '@/components/shared/Dropdown';
import { Checkbox } from '@/components/shared/CheckBox';
import { SMBold, SMRegular, SMSemiBold } from '@/typography/Typography';
import { InstaPaidHouseAccount } from '@/services/PaymentsServiceV2';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useErrorHandling } from '@/utils/errorHandling';

// The backend only accepts these two currencies (validated server-side); the
// dropdown mirrors that fixed set.
const SUPPORTED_CURRENCIES = ['DKK', 'EUR'] as const;

const WarningBox = styled(Box)(({ theme }) => ({
    borderRadius: '8px',
    border: `1px solid ${theme.palette.warning.main}`,
    backgroundColor: theme.palette.warning.light,
    padding: theme.spacing(1.5)
}));

interface CreateHouseAccountDialogProps {
    open: boolean;
    onClose: () => void;
    existingAccounts: InstaPaidHouseAccount[];
    // True only once the house-accounts list query has succeeded. The confirm
    // gate is derived from that list, so create must stay blocked until it is
    // trustworthy — a pending/errored list must never let the gate be skipped.
    listReady: boolean;
}

export const CreateHouseAccountDialog = ({
    open,
    onClose,
    existingAccounts,
    listReady
}: CreateHouseAccountDialogProps) => {
    const { t } = useTranslation();
    const { setAlert } = useNotificationsSnackbar();
    const { backendErrorHandler } = useErrorHandling();
    const { createHouseAccount } = usePaymentsMutations();

    const [name, setName] = useState('');
    const [currency, setCurrency] = useState('');
    const [confirmChecked, setConfirmChecked] = useState(false);
    const [nameTaken, setNameTaken] = useState(false);

    // The confirm gate keys on the page's already-fetched list — no extra
    // request. Only an ACTIVE account of the chosen currency triggers it;
    // inactive same-currency accounts do not degrade auto-binding.
    const hasActiveSameCurrency = useMemo(
        () =>
            !!currency &&
            existingAccounts.some(
                (account) => account.currency === currency && account.isActive
            ),
        [currency, existingAccounts]
    );

    const resetAndClose = () => {
        setName('');
        setCurrency('');
        setConfirmChecked(false);
        setNameTaken(false);
        onClose();
    };

    const canCreate =
        listReady &&
        name.trim().length > 0 &&
        !!currency &&
        (!hasActiveSameCurrency || confirmChecked) &&
        !createHouseAccount.isPending;

    const handleCreate = () => {
        createHouseAccount.mutate(
            { name: name.trim(), currency },
            {
                onSuccess: () => {
                    setAlert(t('house_accounts.create_success'), 'success');
                    resetAndClose();
                },
                onError: (error) => {
                    // Duplicate name is a friendly inline error on the field; the
                    // dialog stays open so the operator can rename and retry.
                    if ((error as AxiosError)?.response?.status === 409) {
                        setNameTaken(true);
                        return;
                    }
                    backendErrorHandler()(error);
                }
            }
        );
    };

    return (
        <StyledDialog open={open} onClose={resetAndClose}>
            <StyledDialogContainer style={{ width: '440px', padding: '24px' }}>
                <SMSemiBold mb={2}>
                    {t('house_accounts.add_account')}
                </SMSemiBold>

                <InputText
                    testid="house-account-name"
                    label={t('house_accounts.name')}
                    required
                    value={name}
                    onChange={(event) => {
                        setName(event.target.value);
                        if (nameTaken) {
                            setNameTaken(false);
                        }
                    }}
                    error={nameTaken}
                    helperText={
                        nameTaken
                            ? t('house_accounts.name_taken_error')
                            : undefined
                    }
                />

                <Box mt={2}>
                    <SMSemiBold mb={0.5}>
                        {t('house_accounts.currency')}
                    </SMSemiBold>
                    <Dropdown
                        label={t('house_accounts.currency')}
                        value={currency}
                        choices={SUPPORTED_CURRENCIES.map((code) => ({
                            label: code,
                            value: code
                        }))}
                        onChange={(value) => {
                            setCurrency(value as string);
                            // Re-arm the gate for the newly chosen currency.
                            setConfirmChecked(false);
                        }}
                    />
                </Box>

                {hasActiveSameCurrency && (
                    <WarningBox mt={2} data-testid="same-currency-warning">
                        <SMRegular>
                            {t('house_accounts.same_currency_warning')}
                        </SMRegular>
                        <FormControlLabel
                            sx={{ mt: 1, alignItems: 'flex-start' }}
                            control={
                                <Checkbox
                                    data-testid="same-currency-confirm"
                                    checked={confirmChecked}
                                    onChange={(event) =>
                                        setConfirmChecked(event.target.checked)
                                    }
                                />
                            }
                            label={
                                <SMRegular>
                                    {t(
                                        'house_accounts.same_currency_confirm_label'
                                    )}
                                </SMRegular>
                            }
                        />
                    </WarningBox>
                )}

                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    gap={1.5}
                    mt={3}
                >
                    <OutlinedSecondaryButtonLG
                        color="secondary"
                        style={{ height: '40px', borderRadius: '8px' }}
                        onClick={resetAndClose}
                    >
                        <SMBold textTransform="none">{t('Cancel')}</SMBold>
                    </OutlinedSecondaryButtonLG>
                    <PrimaryButtonLG
                        style={{ height: '40px', borderRadius: '8px' }}
                        disabled={!canCreate}
                        onClick={handleCreate}
                    >
                        <SMBold textTransform="none">
                            {t('house_accounts.add_account')}
                        </SMBold>
                    </PrimaryButtonLG>
                </Stack>
            </StyledDialogContainer>
        </StyledDialog>
    );
};

export default CreateHouseAccountDialog;
